/**
 * Sentry Browser Initialization
 *
 * Must be imported at the very top of app.tsx (before any React code)
 * so Sentry can capture errors from the earliest point.
 *
 * IMPORTANT: Read process.env.NODE_ENV (and COMMIT_HASH / SENTRY_DSN) directly.
 * Webpack DefinePlugin only replaces those exact member expressions. Using
 * `const env = process.env; env.NODE_ENV` leaves NODE_ENV undefined in the
 * browser bundle, so isProduction was always false and Sentry never enabled.
 *
 * Replay deferral:
 *
 *   `Sentry.replayIntegration()` pulls in the session-replay recorder
 *   — ~150 KB minified, the heaviest single integration in `@sentry/react`.
 *   It only does useful work when an error fires or a session sample
 *   triggers, so it doesn't need to be on the critical path. We start
 *   Sentry with only browser tracing (cheap), then load + register
 *   replay in an `requestIdleCallback` (or `setTimeout` fallback) so
 *   it lands well after first paint without contending for bandwidth
 *   during the cold load. Any errors that fire BEFORE replay registers
 *   are still captured by `Sentry.init()` — they just lack the replay
 *   sidecar.
 */
import * as Sentry from "@sentry/react";
const nodeEnv = process.env.NODE_ENV ?? "development";
const release = process.env.COMMIT_HASH ?? "dev";
const dsnFromEnv = process.env.SENTRY_DSN ?? "";
const isProduction = nodeEnv === "production";
/** Delay before kicking off the replay load. Tuned to land after first paint without competing for connection slots during the cold-load waterfall. */
const REPLAY_LOAD_DELAY_MS = 2000;
if (typeof window !== "undefined") {
    window.SENTRY_RELEASE = { id: release };
}
const dsn = dsnFromEnv ||
    "https://bda8b1803aba810945e8a9682ce1dcd2@o4511017974169600.ingest.us.sentry.io/4511030437412864";
Sentry.init({
    dsn,
    environment: nodeEnv,
    release,
    attachStacktrace: true,
    tracesSampleRate: 0.2,
    enabled: isProduction,
    // Browser tracing only on the critical path. Replay is added below
    // in an idle callback so its ~150 KB chunk doesn't compete with
    // bundle parse or the bootstrap request.
    integrations: [Sentry.browserTracingIntegration?.()].filter((i) => i != null),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});
/**
 * Register session replay after first paint. The integration is the
 * heaviest piece of `@sentry/react` (~150 KB minified) and only does
 * useful work after the user has been in the session for a while, so
 * we pay its cost off the cold-load critical path.
 *
 * Idle-callback scheduling is preferred; `setTimeout` is the Safari
 * fallback. Failure to load is logged but otherwise silent — Sentry
 * still captures errors via the synchronous browserTracingIntegration
 * configured above; the user just doesn't get the replay sidecar on
 * those issues.
 */
function registerReplayWhenIdle() {
    if (isProduction === false)
        return;
    if (typeof window === "undefined")
        return;
    const schedule = typeof window.requestIdleCallback === "function"
        ? (fn) => window.requestIdleCallback(fn, { timeout: REPLAY_LOAD_DELAY_MS * 2 })
        : (fn) => window.setTimeout(fn, REPLAY_LOAD_DELAY_MS);
    schedule(() => {
        void (async () => {
            try {
                const mod = await import(
                /* webpackChunkName: "sentry-replay" */ "@sentry/react");
                const integration = mod.replayIntegration?.();
                if (integration)
                    Sentry.addIntegration(integration);
            }
            catch (e) {
                console.warn("[sentry] failed to load replay integration:", e);
            }
        })();
    });
}
registerReplayWhenIdle();
export { Sentry };
